import React, { Component } from "react";
import SEO from "react-seo-component";
import { Badge, Button, Card, CardBody, Col, Container, Row } from "reactstrap";

class Section extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeFeature: null,
    };
  }

  scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  render() {
    return (
      <React.Fragment>
        <SEO
          title="QikTruck Customer App - South Africa's Premier Logistics Platform"
          titleTemplate="QikTruck"
          titleSeparator="-"
          description="Download the QikTruck customer app for seamless delivery and logistics solutions across Africa. Book vehicles, track orders, and manage deliveries on the go."
          image="https://media.qiktruck.co.za/customer-app.png"
          pathname="https://qiktruck.co.za/app"
          twitterUsername="qiktrucksa"
        />

        {/* Hero Section */}
        <section id="home" className="app-hero">
          <div className="hero-gradient-overlay">
            <Container>
              <Row className="align-items-center min-vh-100">
                <Col lg={6} md={12}>
                  <div className="hero-content text-white">
                    <Badge color="success" pill className="mb-3 px-3 py-2">
                      📱 Available on All Platforms
                    </Badge>
                    <h1 className="hero-title mb-4 text-white">
                      <span className="highlight-text">QikTruck</span><br />
                      Customer App
                    </h1>
                    <p className="hero-subtitle mb-4">
                      South Africa's Premier Logistics Platform
                    </p>
                    <p className="hero-description mb-5">
                      Experience seamless delivery and logistics solutions at your fingertips. 
                      Book vehicles, track orders, and manage deliveries with ease across Africa.
                    </p>
                    <div className="hero-actions">
                      <Button 
                        color="warning" 
                        size="lg" 
                        className="me-3 mb-3"
                        onClick={() => this.scrollToSection('download')}
                      >
                        Download Now
                      </Button>
                      <Button 
                        color="outline-light" 
                        size="lg" 
                        className="mb-3"
                        style={{marginLeft: '10px'}}
                        onClick={() => this.scrollToSection('features')}
                      >
                        See Features
                      </Button>
                    </div>
                    <div className="hero-stats mt-5">
                      <Row>
                        <Col md={4} className="text-center mb-3">
                          <div className="stat-number">500K+</div>
                          <div className="stat-label">Active Users Recorded</div>
                        </Col>
                        <Col md={4} className="text-center mb-3">
                          <div className="stat-number">5000+</div>
                          <div className="stat-label">Vehicles</div>
                        </Col>
                        <Col md={4} className="text-center mb-3">
                          <div className="stat-number">All</div>
                          <div className="stat-label">Cities</div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </Col>
                <Col lg={6} md={12}>
                  <div className="hero-image-container">
                    <div className="phone-mockup">
                      <img 
                        src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=500&q=80" 
                        alt="QikTruck Customer App Interface"
                        className="app-screenshot"
                      />
                    </div>
                    <div className="floating-card">
                      <div className="card-content">
                        <h5>4.8/5 Rating</h5>
                        <p>Trusted by 500K+ customers</p>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </section>

        {/* Key Features Section */}
        <section id="features" className="py-5 bg-light">
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} className="text-center mb-5">
                <h2 className="section-title">Powerful Features</h2>
                <p className="section-subtitle">
                  Everything you need for seamless logistics and delivery management
                </p>
              </Col>
            </Row>
            <Row>
              <Col lg={4} md={6} className="mb-4">
                <Card className="feature-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center p-4">
                    <div className="icon-wrapper mb-3">
                      <i className="fas fa-truck text-warning" style={{fontSize: '2.5rem'}}></i>
                    </div>
                    <h4>Vehicle Booking</h4>
                    <p>Book from bakkies to 16-ton trucks instantly. Real-time availability and transparent pricing.</p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4} md={6} className="mb-4">
                <Card className="feature-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center p-4">
                    <div className="icon-wrapper mb-3">
                      <i className="fas fa-map-marked-alt text-warning" style={{fontSize: '2.5rem'}}></i>
                    </div>
                    <h4>Real-Time Tracking</h4>
                    <p>Track your deliveries in real-time with GPS tracking and live updates from your driver.</p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4} md={6} className="mb-4">
                <Card className="feature-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center p-4">
                    <div className="icon-wrapper mb-3">
                      <i className="fas fa-calculator text-warning" style={{fontSize: '2.5rem'}}></i>
                    </div>
                    <h4>Instant Quotes</h4>
                    <p>Get transparent pricing instantly. No hidden fees, no surprises. Just honest, upfront costs.</p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4} md={6} className="mb-4">
                <Card className="feature-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center p-4">
                    <div className="icon-wrapper mb-3">
                      <i className="fas fa-credit-card text-warning" style={{fontSize: '2.5rem'}}></i>
                    </div>
                    <h4>Secure Payments</h4>
                    <p>Multiple payment options including card, EFT, and mobile payments. All transactions secured.</p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4} md={6} className="mb-4">
                <Card className="feature-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center p-4">
                    <div className="icon-wrapper mb-3">
                      <i className="fas fa-history text-warning" style={{fontSize: '2.5rem'}}></i>
                    </div>
                    <h4>Order History</h4>
                    <p>Access your complete delivery history, invoices, and repeat previous orders with one tap.</p>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={4} md={6} className="mb-4">
                <Card className="feature-card h-100 border-0 shadow-sm">
                  <CardBody className="text-center p-4">
                    <div className="icon-wrapper mb-3">
                      <i className="fas fa-headset text-warning" style={{fontSize: '2.5rem'}}></i>
                    </div>
                    <h4>24/7 Support</h4>
                    <p>Get help anytime with our in-app chat support and dedicated customer service team.</p>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        {/* App Screenshots Section */}
        <section className="py-5">
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} className="text-center mb-5">
                <h2 className="section-title">See It In Action</h2>
                <p className="section-subtitle">
                  Beautiful, intuitive design that makes logistics simple
                </p>
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col lg={4} md={12} className="mb-4">
                <div className="screenshot-item">
                  <img 
                    src="https://images.unsplash.com/photo-1551650975-87deedd944c3?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" 
                    alt="Home Screen"
                    className="screenshot-image"
                  />
                  <h5 className="mt-3">Easy Booking</h5>
                  <p>Select your vehicle type and get instant quotes</p>
                </div>
              </Col>
              <Col lg={4} md={12} className="mb-4">
                <div className="screenshot-item">
                  <img 
                    src="https://images.unsplash.com/photo-1556075798-4825dfaaf498?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" 
                    alt="Tracking Screen"
                    className="screenshot-image"
                  />
                  <h5 className="mt-3">Live Tracking</h5>
                  <p>Follow your delivery every step of the way</p>
                </div>
              </Col>
              <Col lg={4} md={12} className="mb-4">
                <div className="screenshot-item">
                  <img 
                    src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80" 
                    alt="Profile Screen"
                    className="screenshot-image"
                  />
                  <h5 className="mt-3">Manage Orders</h5>
                  <p>View history and manage your deliveries</p>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Benefits Section */}
        <section id="benefits" className="py-5 bg-light">
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} className="text-center mb-5">
                <h2 className="section-title">Why Choose QikTruck?</h2>
                <p className="section-subtitle">
                  The smart choice for modern logistics needs
                </p>
              </Col>
            </Row>
            <Row className="align-items-center mb-5">
              <Col lg={6} md={12} className="mb-4">
                <div className="benefit-content">
                  <Badge color="warning" className="mb-3">Reliability</Badge>
                  <h3>Africa's Largest Fleet</h3>
                  <p className="benefit-description">
                    Access to over 5,000 vehicles across 50+ cities in Africa. From small 
                    deliveries to large cargo transport, we have the right vehicle for every job.
                  </p>
                  <ul className="benefit-list">
                    <li>✓ 5000+ vetted vehicles</li>
                    <li>✓ 50+ cities coverage</li>
                    <li>✓ Same-day delivery available</li>
                    <li>✓ Cargo insurance included</li>
                  </ul>
                </div>
              </Col>
              <Col lg={6} md={12}>
                <img 
                  src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="QikTruck Fleet"
                  className="benefit-image"
                />
              </Col>
            </Row>

            <Row className="align-items-center mb-5">
              <Col lg={6} md={12} className="order-lg-2 mb-4 p-4">
                <div className="benefit-content">
                  <Badge color="primary" className="mb-3">Technology</Badge>
                  <h3>Smart Logistics Platform</h3>
                  <p className="benefit-description">
                    Powered by advanced algorithms that optimize routes, match you with 
                    the best drivers, and ensure timely deliveries every time.
                  </p>
                  <ul className="benefit-list">
                    <li>✓ AI-powered route optimization</li>
                    <li>✓ Smart driver matching</li>
                    <li>✓ Predictive delivery times</li>
                    <li>✓ Real-time price calculation</li>
                  </ul>
                </div>
              </Col>
              <Col lg={6} md={12} className="order-lg-1">
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="Smart Technology"
                  className="benefit-image"
                />
              </Col>
            </Row>

            <Row className="align-items-center">
              <Col lg={6} md={12} className="mb-4">
                <div className="benefit-content">
                  <Badge color="warning" className="mb-3">Support</Badge>
                  <h3>Exceptional Customer Service</h3>
                  <p className="benefit-description">
                    24/7 customer support, dedicated account managers for businesses, 
                    and a commitment to resolving any issues quickly and efficiently.
                  </p>
                  <ul className="benefit-list">
                    <li>✓ 24/7 in-app chat support</li>
                    <li>✓ Dedicated phone support</li>
                    <li>✓ Account managers for businesses</li>
                    <li>✓ 99.5% customer satisfaction</li>
                  </ul>
                </div>
              </Col>
              <Col lg={6} md={12}>
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80" 
                  alt="Customer Support"
                  className="benefit-image"
                />
              </Col>
            </Row>
          </Container>
        </section>

        {/* Download Section */}
        <section id="download" className="py-5">
          <Container>
            <Row className="justify-content-center text-center">
              <Col lg={8}>
                <h2 className="section-title mb-4">
                  Ready to Transform Your Logistics?
                </h2>
                <p className="section-subtitle mb-5">
                  Join hundreds of thousands of satisfied customers across Africa. Download now and experience the future of logistics.
                </p>
                
                <div className="download-buttons mb-5">
                  <a href="https://play.google.com/store/apps/details?id=za.co.qiktruck" className="download-btn me-3 mb-3 d-inline-block">
                    <img 
                      src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                      alt="Download on Google Play"
                      className="download-badge"
                    />
                  </a>
                  <a href="https://apps.apple.com/za/app/qiktruck-bakkie-truck-hire/id6741314137" className="download-btn mb-3 d-inline-block">
                    <img 
                      src="https://qiktruck.co.za/static/media/apple-play-download.3f9ab08ae56699b0acdd1f3adb635d19.svg" 
                      alt="Download on App Store"
                      className="download-badge"
                    />
                  </a>
                </div>

                <div className="qr-section">
                  <p className="mb-3">Or scan the QR code:</p>
                  <div className="qr-code-placeholder">
                    <i className="fas fa-qrcode" style={{fontSize: '4rem', color: '#6b7280'}}></i>
                    <p className="mt-2 text-muted">QR Code</p>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-5 bg-light">
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} className="text-center">
                <h3>Need Help Getting Started?</h3>
                <p className="mb-4">
                  Our customer success team is here to help you make the most of the QikTruck app.
                </p>
              </Col>
            </Row>
          </Container>
        </section>

        <style jsx>{`
          .app-hero {
            background: linear-gradient(135deg, #7c3aed 0%, #f97316 100%);
            position: relative;
            overflow: hidden;
          }
          
          .hero-gradient-overlay {
            background: rgba(0,0,0,0.1);
            min-height: 100vh;
            display: flex;
            align-items: center;
          }
          
          .hero-title {
            font-size: 3.5rem;
            font-weight: 800;
            line-height: 1.1;
          }
          
          .highlight-text {
            color: #f97316;
          }
          
          .hero-subtitle {
            font-size: 1.5rem;
            font-weight: 600;
            opacity: 0.9;
          }
          
          .hero-description {
            font-size: 1.1rem;
            opacity: 0.8;
            line-height: 1.6;
          }
          
          .phone-mockup {
            position: relative;
            max-width: 300px;
            margin: 0 auto;
          }
          
          .app-screenshot {
            width: 100%;
            height: 500px;
            object-fit: cover;
            border-radius: 30px;
            box-shadow: 0 25px 50px rgba(0,0,0,0.3);
            border: 8px solid #1f2937;
          }
          
          .floating-card {
            position: absolute;
            bottom: 20px;
            right: -20px;
            background: white;
            padding: 15px 20px;
            border-radius: 15px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.2);
            min-width: 140px;
          }
          
          .stat-number {
            font-size: 2rem;
            font-weight: 700;
            color: #f97316;
          }
          
          .stat-label {
            font-size: 0.9rem;
            opacity: 0.8;
          }
          
          .section-title {
            font-size: 2.5rem;
            font-weight: 700;
            color: #1f2937;
            margin-bottom: 1rem;
          }
          
          .section-subtitle {
            font-size: 1.2rem;
            color: #6b7280;
            line-height: 1.6;
          }
          
          .feature-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border: none;
          }
          
          .feature-card:hover {
            transform: translateY(-10px);
            box-shadow: 0 20px 40px rgba(0,0,0,0.1) !important;
          }
          
          .screenshot-item {
            text-align: center;
          }
          
          .screenshot-image {
            width: 100%;
            max-width: 250px;
            height: 400px;
            object-fit: cover;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
          }
          
          .benefit-image {
            width: 100%;
            height: 300px;
            object-fit: cover;
            border-radius: 15px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
          }
          
          .benefit-list {
            list-style: none;
            padding-left: 0;
          }
          
          .benefit-list li {
            padding: 8px 0;
            font-size: 1.1rem;
          }
          
          .download-badge {
            height: 60px;
            transition: transform 0.3s ease;
          }
          
          .download-badge:hover {
            transform: scale(1.05);
          }
          
          .qr-code-placeholder {
            display: inline-block;
            padding: 20px;
            border: 2px dashed #d1d5db;
            border-radius: 10px;
            background: #f9fafb;
          }
          
          .contact-options {
            margin-top: 2rem;
          }
          
          @media (max-width: 768px) {
            .hero-title {
              font-size: 2.5rem;
            }
            
            .section-title {
              font-size: 2rem;
            }
            
            .app-screenshot {
              height: 400px;
            }
            
            .screenshot-image {
              height: 300px;
            }
            
            .benefit-image {
              height: 200px;
            }
            
            .floating-card {
              position: static;
              margin-top: 20px;
              text-align: center;
            }
          }
        `}</style>
      </React.Fragment>
    );
  }
}

export default Section; 